<script lang="ts">
  import * as d3 from 'd3';
  import { Tier, getRankTier } from '$lib/er/rank-tiers/calculation';

  const TIER_RGB_TABLE = {
    [Tier.None]: [240, 240, 240],
    [Tier.Iron]: [229, 239, 255],
    [Tier.Bronze]: [252, 149, 59],
    [Tier.Silver]: [96, 127, 161],
    [Tier.Gold]: [224, 184, 102],
    [Tier.Platinum]: [122, 225, 175],
    [Tier.Diamond]: [208, 196, 226],
    [Tier.Meteorite]: [105, 159, 229],
    [Tier.Mithril]: [130, 208, 225],
    [Tier.Demigod]: [255, 255, 222],
    [Tier.Eternity]: [160, 31, 74],
  };

  export let history: number[];
  export let count: number;
  export let tick: number;
  export let lastDate: number;
  export let seasonId: number;

  const width = 330;
  const height = 160;
  const marginLeft = 40;
  const marginBottom = 30;

  const tickSize = 5;
  const length = count * tick;
  const xScale = d3.scaleUtc(
    [lastDate - 86400000 * length, lastDate],
    [marginLeft + tickSize, width - tickSize],
  );
  const xTicks = xScale.ticks(length);
  const min = Math.min(...history);
  const max = Math.max(...history);
  const yTick = Math.ceil(Math.max(1, max - min) / 700) * 100;
  const yDomain = [
    (Math.floor(min / yTick) - 1) * yTick,
    (Math.ceil(max / yTick) + 1) * yTick,
  ] as const;
  const yCount = (yDomain[1] - yDomain[0]) / yTick;
  const yScale = d3.scaleLinear(yDomain, [height - marginBottom, tickSize]);
  const emptyTickCount = xTicks.length - history.length;
  const line = d3
    .line<number>()
    .x((_, i) => xScale(xTicks[i + emptyTickCount]))
    .y((v) => yScale(v))(history);
  const disp = (history.length << 1) - xTicks.length - 1;
  const points = [...history].reverse().map((v, i) => {
    const {
      tier: { tierEnum },
    } = getRankTier({
      mmr: v,
      seasonId,
    });
    return {
      x: xScale(xTicks[disp - i]),
      y: yScale(v),
      v,
      tier: tierEnum,
    };
  });
</script>

<svg {width} {height} viewBox="0 0 {width} {height}" overflow="visible">
  <g transform="translate(0, {height - marginBottom})">
    {#each xTicks as x, i}
      {#if i % tick === 0}
        {@const scale = xScale(x)}
        <line
          stroke="white"
          stroke-opacity={scale === marginLeft + tickSize ? 0.7 : 0.2}
          x1={scale}
          x2={scale}
          y1={marginBottom - height}
          y2={5}
        />

        <text fill="rgba(255, 255, 255, 0.7)" text-anchor="middle" x={scale} y={22}>
          {x.getUTCMonth() + 1}/{x.getUTCDate()}
        </text>
      {/if}
    {/each}
  </g>

  <g transform="translate({marginLeft}, 0)">
    {#each yScale.ticks(yCount) as y}
      {@const scale = yScale(y)}
      <line
        stroke="white"
        stroke-opacity={scale === height - marginBottom ? 0.7 : 0.2}
        x1={0}
        x2={width - marginLeft}
        y1={scale}
        y2={scale}
      />

      {@const {
        tier: { tierEnum },
      } = getRankTier({ mmr: y, seasonId })}
      <text
        fill="rgb({TIER_RGB_TABLE[tierEnum]})"
        text-anchor="end"
        dominant-baseline="middle"
        x={-9}
        y={scale}
      >
        {y}
      </text>
    {/each}
  </g>

  <path fill="none" stroke="white" stroke-opacity="0.8" stroke-width="3" d={line} />

  {#each points as { x, y, v, tier }, i}
    {#if !i || v !== points[i - 1].v}
      {@const fill = `rgb(var(--color-tier-${Tier[tier].toLowerCase()}-rgb))`}
      <g fill="white" transform="translate({x} {y})">
        <text text-anchor="middle" font-size="8" {fill} y="-8">{v}</text>
        <circle r="4" {fill} />
      </g>
    {/if}
  {/each}
</svg>

<style lang="postcss">
  svg {
    max-width: 100%;
    height: auto;
    user-select: none;
  }
</style>
